import { FC, ReactNode, useState } from 'react';
import { Button, IconButton, InlineNotification } from '@carbon/react';
import {
  CaretLeft,
  CaretRight,
  ChevronDown,
  ChevronUp,
  Pin,
  PinFilled,
} from '@carbon/icons-react';
import ErrorBoundary from 'components/route/error-boundary';
import { useCardPin } from './card-slot';
import styles from './workspace-card.module.scss';

export interface WellScope {
  /** The scoped well's name, shown beside the card title. */
  wellName: string;
  /** How the scope reads — the Map card says "scoped to X" rather than just "X". */
  prefix?: string;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

interface Props {
  /**
   * The card's name. Always used for the pin control's label; rendered as the card's
   * heading unless `titleHidden` says the body supplies its own.
   */
  title: string;
  /**
   * The per-card well stepper. Omitted for a card that isn't well-scoped, and for a
   * single-well workspace there is nothing to step to — the name still shows.
   */
  scope?: WellScope;
  /** Right-hand header controls, if the card has any. */
  actions?: ReactNode;
  /**
   * Rendered immediately after the title, before the well stepper — the workspace's
   * "Edited" pill, which marks a card whose well carries a staged edit.
   */
  titleSuffix?: ReactNode;
  /**
   * Suppresses the card's own heading, for a body that already renders one — the Well
   * page hosts two such sections (Related Items, Related Well Files) whose components
   * own their `Section` header, and a card heading above them would just repeat it.
   */
  titleHidden?: boolean;
  children: ReactNode;
}

/**
 * The shell every workspace detail card sits in: the white panel, and a header with the
 * title, the inline well stepper, and any card-level actions.
 *
 * The mock repeats the stepper per card header rather than once above the page, so the
 * scoped well reads without scrolling up. All of them drive one selection.
 * @param {Props} props The card title, optional scope and actions, and its body.
 * @return {JSX.Element} The card.
 */
export const WorkspaceCard: FC<Props> = ({
  title,
  scope,
  actions,
  titleSuffix,
  titleHidden,
  children,
}) => {
  // Pinning is published by the CardSlot this card is rendered in, so no card has to
  // pass pin props down. Null for a card rendered outside one.
  const pin = useCardPin();
  // Collapsed hides the body but keeps it mounted — these bodies are hosted production
  // components that do not survive a remount (see CardSlot's `hidden`) — so expanding
  // brings the card back exactly as it was.
  const [collapsed, setCollapsed] = useState(false);

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        {/* Skipped entirely for a body that owns its heading and has no stepper —
            an empty <h3> would still take up the header's line-height. */}
        {(!titleHidden || scope || titleSuffix) && (
          <h3 className={styles.title}>
            {titleHidden ? null : title}
            {titleSuffix}
            {scope && (
              <span className={styles.wellNav}>
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={CaretLeft}
                  iconDescription="Previous well"
                  disabled={!scope.hasPrevious}
                  onClick={scope.onPrevious}
                />
                {scope.prefix
                  ? `· ${scope.prefix} ${scope.wellName}`
                  : `· ${scope.wellName}`}
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={CaretRight}
                  iconDescription="Next well"
                  disabled={!scope.hasNext}
                  onClick={scope.onNext}
                />
              </span>
            )}
          </h3>
        )}
        <div className={styles.actions}>
          {/* The card's own controls, then the collapse, then the pin at the far right
              — every card header reads the same way (UAT). */}
          {actions}
          <IconButton
            kind="ghost"
            size="md"
            align="bottom-right"
            label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((value) => !value)}
          >
            {collapsed ? <ChevronDown /> : <ChevronUp />}
          </IconButton>
          {pin && (
            <IconButton
              kind="ghost"
              size="md"
              align="bottom-right"
              label={
                pin.pinned ? `Unpin ${title} from top` : `Pin ${title} to top`
              }
              onClick={pin.toggle}
            >
              {pin.pinned ? <PinFilled /> : <Pin />}
            </IconButton>
          )}
        </div>
      </div>
      {/* Each section is boundaried on its own. These bodies are hosted production
        components — several are lazily imported and one is a very large module graph —
        and without this a single failed chunk or throw replaces the whole workspace with
        the app's error screen instead of degrading one card. */}
      <ErrorBoundary
        fallback={
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={`${title} could not be loaded`}
            subtitle="Reload the page to try again. The rest of the workspace is unaffected."
          />
        }
      >
        <div hidden={collapsed}>{children}</div>
      </ErrorBoundary>
    </section>
  );
};

/**
 * A card body for a section that is a placeholder frame rather than wired data.
 * @param {{ children: ReactNode }} props The note to show in place of the section.
 * @return {JSX.Element} The note.
 */
export const WorkspaceCardNote: FC<{ children: ReactNode }> = ({
  children,
}) => <p className={styles.note}>{children}</p>;
